import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/forgot-password', { email }, {
        headers: { 'Content-Type': 'application/json' }
      });

      setSent(true);
      toast.success('Email inviata!');
    } catch (err) {
      // Non rivelare se l'email esiste o meno per sicurezza
      // Mostriamo sempre un messaggio positivo
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  // Schermata di conferma invio
  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Controlla la tua email</h2>
            <p className="text-gray-600 mb-6">
              Se l'indirizzo <strong>{email}</strong> è registrato nel sistema, 
              riceverai un'email con le istruzioni per reimpostare la password.
            </p>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Non hai ricevuto l'email? Controlla la cartella spam o
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSent(false);
                  setEmail('');
                }}
                className="w-full"
              >
                Riprova con un altro indirizzo
              </Button>
              <Link to="/login">
                <Button variant="link" className="w-full text-[#FF7819]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Torna al login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src="/images/abitalo-logo.png" 
              alt="ABITALO"
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl">Password Dimenticata?</CardTitle>
          <CardDescription>
            Inserisci la tua email e ti invieremo un link per reimpostare la password
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email di accesso"
                  className="pl-10"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Errore */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <Button
              type="submit"
              className="w-full bg-[#FF7819] hover:bg-[#e66a0f] text-white"
              disabled={loading || !email}
            >
              {loading ? 'Invio in corso...' : 'Invia Link di Reset'}
            </Button>

            {/* Back to login */}
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full text-gray-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna al login
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
